import { IconCode } from "@/assets/icons/geist/IconCode";
import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Tooltip } from "@/components/Tooltip";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { DiffViewer } from "@/components/ui/DiffViewer";
import type { AgentMessageFragmentFileModification } from "@/hooks/use-stream-conversation";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { calculateDiff, calculateDiffStats, type DiffGroup } from "@/utils/diff";
import { Check, Copy } from "lucide-react";
import { useCallback, useMemo, type FC } from "react";

export const FragmentFileModification: FC<{
    fragment: AgentMessageFragmentFileModification;
    defaultOpen?: boolean;
    onHandleReveal?: () => void;
}> = ({ fragment, defaultOpen = true, onHandleReveal }) => {
    const diff = useMemo(() => {
        // Use structured diff if available, otherwise parse content
        if (fragment.diff) {
            return {
                search: "", // Not needed for rendering
                replace: "", // Not needed for rendering
                stats: fragment.diff.stats,
                diffGroups: fragment.diff.diffGroups,
            };
        }
        return parseDiffContent(fragment.content);
    }, [fragment.content, fragment.diff]);

    return (
        <Collapsible defaultOpen={defaultOpen}>
            <div
                className={cn(
                    "flex flex-col overflow-hidden rounded-lg border border-border-base bg-surface-muted px-1.5 py-1.5",
                )}
            >
                <FragmentFileModificationHeader
                    filePath={fragment.filePath}
                    diff={diff}
                    onHandleReveal={onHandleReveal}
                />
                <CollapsibleContent>
                    <FragmentFileModificationDiff fragment={fragment} diff={diff} />
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};

const FragmentFileModificationHeader: FC<{
    filePath: string;
    diff?: SearchReplaceDiff;
    onHandleReveal?: () => void;
}> = ({ filePath, diff, onHandleReveal }) => {
    const [copied, setCopied] = useTemporaryState(false, 2000);

    const handleCopyPath = useCallback(
        async (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            try {
                await navigator.clipboard.writeText(filePath);
                setCopied(true);
            } catch (error) {
                setCopied(false);
                toast({
                    title: "Failed to copy file path",
                    description: formatError(error),
                });
            }
        },
        [filePath, setCopied],
    );

    return (
        <div className={"flex h-8 items-center justify-between px-2 py-0"}>
            <div className="group/path flex min-w-0 items-center gap-2">
                {onHandleReveal ? (
                    <Button
                        variant="ghost"
                        size="xs"
                        LeadingIcon={IconCode}
                        className="shrink-0 text-content-secondary"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onHandleReveal?.();
                        }}
                        title="Open in VS Code"
                        data-tracking-id="open-file-in-vscode"
                    />
                ) : (
                    <IconCode size="base" className="shrink-0 text-content-secondary" />
                )}
                <Text className="select-text truncate text-sm font-bold text-content-secondary">{filePath}</Text>
                <Tooltip content={copied ? "Copied!" : "Copy file path"}>
                    <Button
                        variant="ghost"
                        size="xs"
                        LeadingIcon={() =>
                            copied ? (
                                <Check className="text-content-positive" size={14} />
                            ) : (
                                <Copy size={14} />
                            )
                        }
                        className="shrink-0 opacity-0 transition-opacity group-hover/path:opacity-100"
                        onClick={handleCopyPath}
                        aria-label="Copy file path"
                        title="Copy file path"
                        data-tracking-id="copy-file-path"
                    />
                </Tooltip>
            </div>
            <CollapsibleTrigger asChild>
                <button
                    className="flex shrink-0 cursor-pointer items-center gap-1 rounded px-1 text-sm hover:bg-surface-secondary"
                    data-tracking-id="toggle-file-diff"
                >
                    {diff?.stats && (
                        <>
                            <span className="text-content-positive">+{diff.stats.additions}</span>
                            <span className="text-content-red">-{diff.stats.deletions}</span>
                        </>
                    )}
                </button>
            </CollapsibleTrigger>
        </div>
    );
};

const FragmentFileModificationDiff: FC<{
    fragment: AgentMessageFragmentFileModification;
    diff: SearchReplaceDiff;
}> = ({ fragment, diff }) => {
    return (
        // border-t border-border-base p-0
        <ScrollArea
            orientation="both"
            className={cn(
                "flex max-h-[300px] rounded-lg border border-border-base bg-surface-primary pb-0 pr-0 [scrollbar-gutter:auto]",
            )}
        >
            <div className="flex flex-grow flex-col" data-testid={`fragment-diff-${fragment.actionId}`}>
                <pre className="flex flex-col">
                    <DiffViewer diffGroups={diff.diffGroups} />
                </pre>
            </div>
        </ScrollArea>
    );
};

type SearchReplaceDiff = {
    search: string;
    replace: string;
    stats: {
        additions: number;
        deletions: number;
    };
    diffGroups: DiffGroup[];
};

const parseDiffContent = (content: string): SearchReplaceDiff => {
    const searchMarker = "<<<<<<< SEARCH";
    const separatorMarker = "=======";
    const replaceMarker = ">>>>>>> REPLACE";

    const searchStart = content.indexOf(searchMarker);
    const separatorStart = content.indexOf(separatorMarker);
    const replaceEnd = content.indexOf(replaceMarker);

    if (searchStart === -1 || separatorStart === -1 || replaceEnd === -1) {
        return {
            search: "",
            replace: "",
            stats: { additions: 0, deletions: 0 },
            diffGroups: [],
        };
    }

    const searchContent = content.slice(searchStart + searchMarker.length, separatorStart).trim();
    const replaceContent = content.slice(separatorStart + separatorMarker.length, replaceEnd).trim();

    // Calculate diff groups once and reuse for both stats and rendering
    const diffGroups = calculateDiff(searchContent, replaceContent);
    const stats = calculateDiffStats(diffGroups);

    return {
        search: searchContent,
        replace: replaceContent,
        stats,
        diffGroups,
    };
};
